import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router';
import {
  Download,
  Users,
  Building2,
  CreditCard,
  Wallet,
  BarChart3,
  Presentation,
} from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';
import { useCityLabel } from '@/i18n/use-city-label';
import { useTranslations } from '@/i18n/use-locale';
import { useLocalizedName } from '@/i18n/localized-name';
import { formatCount, useFormatSar } from '@/lib/format';
import { QueryGate } from '@/components/ui/query-gate';
import {
  useAnalyticsSummaryQuery,
  useClubActivityQuery,
  useMostSearchedQuery,
  useReportQuery,
} from '@/lib/queries';
import { useReportExportMutation } from '@/lib/mutations';
import { useEnumLabel } from '@/lib/labels';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/admin/page-header';
import { DataTable, type DataTableColumn } from '@/components/admin/data-table';
import type { ClubActivity } from '@/contracts';
import { BreakdownCard } from './breakdown-card';
import { isReportPeriod, periodRange, type ReportPeriod } from './report-period';
import { useBreakdowns } from './use-breakdowns';

export function ReportsRoute() {
  const t = useTranslations('admin.reports');
  const kindLabel = useEnumLabel('admin.reports.kind');
  const cityLabel = useCityLabel();
  const localizedName = useLocalizedName();
  const sar = useFormatSar();
  const [period, setPeriod] = useState<ReportPeriod>('month');
  const range = useMemo(() => periodRange(period), [period]);

  const reportQuery = useReportQuery(range.from, range.to);

  // Both panels below the KPIs. Not windowed by the period selector — "most
  // active clubs" and "most searched roles" are live totals, not a range — so
  // they load once and degrade to an empty state rather than blocking the page.
  const clubActivityQuery = useClubActivityQuery();
  const mostSearchedQuery = useMostSearchedQuery();
  // The two all-time breakdowns. Same degrade-to-nothing rule as the panels above: the
  // windowed figures are the page, the tiles are extra.
  const { byCategory, byKind } = useBreakdowns(useAnalyticsSummaryQuery().data);

  const exportCsv = useReportExportMutation({
    okToast: t('exported'),
    failToast: t('exportFailed'),
  });

  return (
    <QueryGate query={reportQuery}>
      {(rep) => {
        const clubs = clubActivityQuery.data ?? [];
        const specialties = mostSearchedQuery.data ?? [];
        const clubColumns: ReadonlyArray<DataTableColumn<ClubActivity>> = [
          {
            key: 'org',
            header: t('activeOrgs.columns.org'),
            cell: (club) => (
              <>
                <p className="font-bold text-navy">{club.name}</p>
                <p className="text-[11.5px] text-conc-500">
                  {kindLabel(club.kind)}
                  {cityLabel(club.city) ? ` · ${cityLabel(club.city)}` : ''}
                </p>
              </>
            ),
          },
          {
            key: 'opened',
            header: t('activeOrgs.columns.opened'),
            headClassName: 'text-center',
            cellClassName: 'num text-center font-semibold text-navy',
            cell: (club) => club.profilesOpened,
          },
          {
            key: 'shortlisted',
            header: t('activeOrgs.columns.shortlisted'),
            headClassName: 'text-center',
            cellClassName: 'num text-center font-semibold text-navy',
            cell: (club) => club.shortlisted,
          },
        ];
        const maxDemand = specialties.reduce((m, s) => Math.max(m, s.demand), 0) || 1;
        const revenueDisplay = sar(rep.revenueMinor / 100);

        return (
          <div className="space-y-6">
            <PageHeader
              title={t('pageTitle')}
              sub={t('pageHint')}
              actions={
                <>
                  <select
                    value={period}
                    onChange={(e) => {
                      if (isReportPeriod(e.target.value)) setPeriod(e.target.value);
                    }}
                    className="rounded-lg border border-conc-100 bg-white px-3.5 py-2 text-[13px] font-semibold text-navy outline-none focus:border-pitch"
                  >
                    <option value="month">{t('period.month')}</option>
                    <option value="quarter">{t('period.quarter')}</option>
                    <option value="year">{t('period.year')}</option>
                  </select>
                  <Link
                    to={`/reports/stats?period=${period}`}
                    className="flex items-center gap-2 rounded-lg border border-conc-100 bg-white px-4 py-2 text-[13px] font-bold text-navy transition hover:border-pitch"
                  >
                    <Presentation aria-hidden className="h-4 w-4" />
                    {t('statsView')}
                  </Link>
                  <Button
                    type="button"
                    onClick={() => exportCsv.mutate(range)}
                    disabled={exportCsv.isPending}
                    className="py-2"
                  >
                    <Download aria-hidden className="h-4 w-4" />
                    {t('export')}
                  </Button>
                </>
              }
            />

            {/* 4 Summary Stat Cards — all from the windowed report */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Card className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-conc-500">
                    {t('newTalents')}
                  </span>
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-pitch-50 text-pitch">
                    <Users className="h-5 w-5" />
                  </span>
                </div>
                <p className="num mt-2 text-2xl font-bold text-navy">{rep.newTalents}</p>
              </Card>

              <Card className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-conc-500">
                    {t('newOrganizations')}
                  </span>
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-navy-50 text-navy">
                    <Building2 className="h-5 w-5" />
                  </span>
                </div>
                <p className="num mt-2 text-2xl font-bold text-navy">{rep.newOrganizations}</p>
              </Card>

              <Card className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-conc-500">
                    {t('newSubscriptions')}
                  </span>
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-navy-50 text-navy">
                    <CreditCard className="h-5 w-5" />
                  </span>
                </div>
                <p className="num mt-2 text-2xl font-bold text-navy">{rep.newSubscriptions}</p>
              </Card>

              <Card className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-conc-500">
                    {t('totalRevenue')}
                  </span>
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-pitch-50 text-pitch">
                    <Wallet className="h-5 w-5" />
                  </span>
                </div>
                <p className="num mt-2 text-2xl font-bold text-navy">{revenueDisplay}</p>
              </Card>
            </div>

            {/* All-time breakdowns: talents by category, organizations by kind */}
            {(byCategory.length > 0 || byKind.length > 0) && (
              <div className="grid gap-6 lg:grid-cols-2">
                {byCategory.length > 0 && (
                  <BreakdownCard
                    heading={t('byCategory.heading')}
                    hint={`${t('byCategory.hint')} · ${t('allTime')}`}
                    rows={byCategory}
                  />
                )}
                {byKind.length > 0 && (
                  <BreakdownCard
                    heading={t('byKind.heading')}
                    hint={`${t('byKind.hint')} · ${t('allTime')}`}
                    rows={byKind}
                  />
                )}
              </div>
            )}

            {/* Main Grid: Club Activity & Top Specialties */}
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Club Activity Table */}
              <Card className="p-5 lg:col-span-2">
                <div className="mb-4">
                  <h3 className="text-[15px] font-bold text-navy">{t('activeOrgs.heading')}</h3>
                  <p className="text-[12.5px] text-conc-500">{t('activeOrgs.hint')}</p>
                </div>

                <DataTable
                  caption={t('activeOrgs.heading')}
                  rows={clubs}
                  columns={clubColumns}
                  rowKey={(club) => club.organizationId}
                  empty={
                    <EmptyState
                      icon={BarChart3}
                      title={t('activeOrgs.emptyTitle')}
                      hint={t('activeOrgs.emptyHint')}
                    />
                  }
                />
              </Card>

              {/* Most Searched Specialties */}
              <Card className="p-5">
                <h3 className="mb-1 text-[15px] font-bold text-navy">{t('topSpecs.heading')}</h3>
                <p className="mb-4 text-[12.5px] text-conc-500">{t('topSpecs.byClubs')}</p>

                <div className="space-y-4">
                  {specialties.map((spec, i) => (
                    <div key={spec.specialtyId} className="space-y-1">
                      <div className="flex justify-between text-[12.5px]">
                        <span className="font-semibold text-navy">
                          <span className="num me-1.5 text-conc-400">#{i + 1}</span>
                          {localizedName(spec)}
                        </span>
                        <span className="num font-bold text-conc-600">
                          {t('topSpecs.searchCount', { count: formatCount(spec.demand) })}
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-conc-100">
                        <div
                          className="h-full rounded-full bg-pitch"
                          style={{
                            width: `${Math.max(6, Math.round((spec.demand / maxDemand) * 100))}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  {specialties.length === 0 && (
                    <p className="py-8 text-center text-[13px] text-conc-400">
                      {t('topSpecs.noSearches')}
                    </p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        );
      }}
    </QueryGate>
  );
}
